// 9fbef606107a605d69c0edbcd8029e5d
/** @module LabeledRadioButton
 * @summary A React component rendering an RadioButton field
 *
 */
import React from 'react';
import { PropTypes } from 'prop-types';
import {
  GLOBAL_COMMON_BROWSE_LABELS,
  globalCommonBrowseLabels,
} from '@tcp/core/src/utils/labels/globalCommonBrowseLabels.labels';
import { getLabelFromCopyOrFromTranslation } from '@tcp/core/src/utils/labels/labelUtilty';
import cssClassName from '../../../../../utils/cssClassName';

/**
 * @summary A React component rendering an checkbox field with a  optional data .
 *
 * Supports <code>input</code> and <code>meta</code> props passed down by a wrappping {@linkcode module:redux-form.Field} HOC.
 * Any extra props (i.e., other than <code>title, subTitle, type, className, meta</code>),
 * e.g., <code>value, disabled, placeholder</code>, passed to this component will be passed along to the rendered <code>input</code> element.
 */
export default class LabeledRadioButton extends React.Component {
  static propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    className: PropTypes.string,
    /** The value of the radioButton (i.e., the value of the associated form field when this radio button is checked) */
    selectedValue: PropTypes.string.isRequired,
    input: PropTypes.shape({
      value: PropTypes.string,
    }),
    children: PropTypes.shape({}),
    disabled: PropTypes.bool,
    checked: PropTypes.bool,
    style: PropTypes.shape({}),
    newQVDesign: PropTypes.bool,
    isNewPDPEnabled: PropTypes.bool,
    isFitNewDesign: PropTypes.bool,
    onKeyDown: PropTypes.func,
  };

  static defaultProps = {
    title: 'Size: ',
    subtitle: [''],
    className: '',
    input: {
      value: {
        name: '',
      },
    },
    children: {},
    disabled: false,
    checked: false,
    style: {},
    newQVDesign: false,
    isNewPDPEnabled: false,
    isFitNewDesign: false,
    onKeyDown: () => {},
  };

  static labeledRadioButtonCounter = 0;

  constructor(props) {
    super(props);

    this.state = {
      isFocused: false,
    };

    LabeledRadioButton.labeledRadioButtonCounter += 1;
    this.labeledRadioButtonCounter = LabeledRadioButton.labeledRadioButtonCounter;
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.isSelected = this.isSelected.bind(this);
  }

  handleFocus(event) {
    this.setState({
      isFocused: true,
    });

    const { input } = this.props;

    if (input && input.onFocus) input.onFocus(event);
  }

  handleBlur(event) {
    this.setState({
      isFocused: false,
    });

    const { input } = this.props;

    if (input && input.onBlur) input.onBlur(event);
  }

  isSelected(input, selectedValue) {
    return input ? selectedValue === input.name : false;
  }

  checkSoftDisabled(softDisable) {
    return softDisable ? 'soft-disabled-new' : '';
  }

  getInputChecked(input, selectedValue, checked, disabled) {
    return !disabled && ((input && selectedValue === input.name) || checked);
  }

  getClassNames(inputChecked, disabled, isFocused, children, className) {
    const containingClassName = cssClassName('label-radio input-radio ', className);
    const inputContainerClassName = cssClassName({
      'input-radio-disabled ': disabled,
      'input-radio-icon-checked ': inputChecked,
      'input-radio-icon-unchecked ': !inputChecked,
      'input-radio-focused': !children && isFocused,
    });
    const inputChildrenClassName = cssClassName({
      'input-radio-title ': true,
      'input-radio-focused ': children && isFocused,
    });
    return { containingClassName, inputContainerClassName, inputChildrenClassName };
  }

  getAriaLabelValue({ input, selectedValue, disabled, groupTitle }) {
    if (disabled && groupTitle && groupTitle.toLowerCase() === 'size') {
      const outOfStockCaps = getLabelFromCopyOrFromTranslation(
        `${GLOBAL_COMMON_BROWSE_LABELS}.lbl_common_outOfStockCaps`,
        globalCommonBrowseLabels
      );
      return outOfStockCaps?.toLowerCase();
    }

    return this.isSelected(input, selectedValue) ? 'selected' : 'not selected';
  }

  getAccessibilityGroupTitle({ groupTitle }) {
    return groupTitle || '';
  }

  render() {
    const {
      title,
      subtitle,
      className,
      meta /* When using redux-form, is passed implicitly by a wrapping Field component */,
      children,
      input,
      disabled,
      selectedValue,
      checked,
      style,
      newQVDesign,
      isFitNewDesign,
      softDisable,
      groupTitle,
      ...otherProps // all the extra props passed to this component (or the wrapping Field component when using redux-form)
    } = this.props;

    const { isFocused } = this.state;
    const inputChecked = this.getInputChecked(input, selectedValue, checked, disabled);
    const { containingClassName, inputContainerClassName, inputChildrenClassName } =
      this.getClassNames(inputChecked, disabled, isFocused, children, className);
    const name = input && input.name ? input.name : otherProps.name;
    const id = `${name}_${this.labeledRadioButtonCounter}`;
    const newCls = this.checkSoftDisabled(softDisable);
    const { onKeyDown } = this.props;
    return (
      <label htmlFor={id} className={containingClassName} style={style}>
        <div
          className={`${
            newQVDesign ? 'quick-view-drawer-redesign' : ''
          } ${inputContainerClassName}`}
        >
          <input
            {...input}
            {...otherProps}
            id={id}
            disabled={disabled}
            type="radio"
            className={disabled || softDisable ? 'disabled' : null}
            value={selectedValue}
            checked={input ? selectedValue === input.name : checked}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            aria-describedby={id}
          />
        </div>
        {children && (
          <div
            role="radio"
            tabIndex={!disabled ? 0 : -1}
            aria-disabled={disabled}
            onKeyDown={onKeyDown}
            aria-checked={this.isSelected(input, selectedValue) ? 'true' : 'false'}
            className={`${
              newQVDesign ? 'quick-view-drawer-redesign' : ''
            } ${inputChildrenClassName} ${newCls}`}
            aria-label={`${this.getAccessibilityGroupTitle({ groupTitle })} ${
              groupTitle === 'color' ? selectedValue : children
            } ${this.getAriaLabelValue({ input, selectedValue, disabled, groupTitle })}`}
          >
            {isFitNewDesign ? children?.toLowerCase() : children}
          </div>
        )}
      </label>
    );
  }
}
